import React, {
  useState,
  useRef,
  useEffect,
  createContext,
  useContext,
} from "react";
import { Link, useParams, useNavigate, useLocation } from "react-router-dom";
import "./style.css";

import Popup from "../components/popup.js";
import { Helmet } from "react-helmet-async";
import useFetch from "../hooks/useFetch.js";
import Lottie from "react-lottie";
import loading from "../pages/loading.json";
import Sidebar, { SearchContext } from "../pages/SidebarAcademy.js";
import Header from "../pages/HeaderAcademy.js";

function titleToSlug(title) {
  return title
    .toLowerCase()
    .replace(/[^\w\s-]/g, "")
    .replace(/[\s_-]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

const ProfessionalCourse = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchTerm, setSearchTerm] = useState("");
  const [isFromSearch, setIsFromSearch] = useState(false); // Track if navigation came from search

  // Fetch academy data
  const {
    loading: academyLoading,
    data: academyData,
    error: academyError,
  } = useFetch(
    `https://devrw2.blob.core.windows.net/rw-website/strapi-data/academy/data.json`
  );

  // Get search term from URL params if navigated with search
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const searchParam = urlParams.get("search");
    const fromSearch = urlParams.get("fromSearch"); // Add this parameter when navigating from search

    if (searchParam) {
      setSearchTerm(searchParam);
      setIsFromSearch(fromSearch === "true");
    }
  }, [location.search]);

  // Update URL when search term changes (optional - for bookmarkable searches)
  useEffect(() => {
    if (searchTerm.trim()) {
      const newUrl = `${location.pathname}?search=${encodeURIComponent(
        searchTerm
      )}${isFromSearch ? "&fromSearch=true" : ""}`;
      window.history.replaceState(null, "", newUrl);
    } else {
      window.history.replaceState(null, "", location.pathname);
      setIsFromSearch(false);
    }
  }, [searchTerm, location.pathname, isFromSearch]);

  const defaultOptions = {
    loop: true,
    autoplay: true,
    animationData: loading,
    rendererSettings: {
      preserveAspectRatio: "xMidYMid slice",
    },
  };

  if (academyLoading) {
    return (
      <div className="w-full flex justify-center items-center min-h-screen">
        <Lottie options={defaultOptions} height={400} width={400} />
      </div>
    );
  }

  const academyItems = Array.isArray(academyData) ? academyData : [];

  // Enhanced filtering logic
  const filteredCourses = academyItems.filter((academy) => {
    // First filter by category
    if (academy.academyCategory !== "professional Growth Courses") return false;

    // If no search term, show all courses in this category
    if (!searchTerm.trim()) return true;

    // If navigation came from search (Enter key press), show only exact match
    if (isFromSearch && searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      return academy.academyTitle?.toLowerCase().includes(searchLower);
    }

    // For regular search (typing in sidebar), show all matching courses
    const searchLower = searchTerm.toLowerCase();
    return (
      academy.academyTitle?.toLowerCase().includes(searchLower) ||
      academy.academyDesc?.toLowerCase().includes(searchLower) ||
      academy.academyCategory?.toLowerCase().includes(searchLower)
    );
  });

  // If from search, show only the best match (first match)
  const coursesToShow =
    isFromSearch && filteredCourses.length > 0
      ? [filteredCourses[0]]
      : filteredCourses;

  const handleClearSearch = () => {
    setSearchTerm("");
    setIsFromSearch(false);
    // Update URL to remove search params
    window.history.replaceState(null, "", location.pathname);
  };

  return (
    <SearchContext.Provider value={{ searchTerm, setSearchTerm }}>
      <div className="bg-gray-50 font-display">
        <Header />
        <div className="px-4 py-8 flex flex-col md:flex-row">
          <Sidebar searchContextValue={{ searchTerm, setSearchTerm }} />
          <div className="md:w-4/5 2xl:w-full">
            <div className="p-4 mb-8 ">
              <div className="mb-2">
                <h2 className="text-2xl font-bold text-left text-black">
                  Digital Skills Courses (57)
                </h2>
              </div>
              <div className="mb-4">
                <h2 className="text-lg font-medium text-left text-gray-900">
                  For Individuals (40)
                </h2>
              </div>
              <h2 className="text-lg font-medium text-left text-gray-900">
                Professional Growth Courses (18)
              </h2>

              {coursesToShow.length === 0 && searchTerm ? (
                <div className="text-center py-12">
                  <div className="text-gray-500 text-lg mb-2">
                    No courses found
                  </div>
                  <div className="text-gray-400 text-sm">
                    Try adjusting your search terms or{" "}
                    <button
                      onClick={handleClearSearch}
                      className="text-blue-600 hover:text-blue-800 underline"
                    >
                      view all courses
                    </button>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-4 mt-3">
                  {coursesToShow.map((academy) => (
                    <BlogCard key={academy.id} academy={academy} />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        <section className="mt-20">
          <Popup utmContent={"blog"} />
        </section>
      </div>
    </SearchContext.Provider>
  );
};

const BlogCard = ({ academy }) => {
  const titleRef = useRef();
  const [titleLines, setTitleLines] = useState(1);
  const { searchTerm } = useContext(SearchContext);

  useEffect(() => {
    if (titleRef.current) {
      const lineHeight = parseInt(
        window.getComputedStyle(titleRef.current).lineHeight
      );
      const titleHeight = titleRef.current.clientHeight;
      const lines = Math.ceil(titleHeight / lineHeight);
      setTitleLines(lines);
    }
  }, [titleRef.current]);

  const descriptionLines = 7 - titleLines;

  // Function to highlight search terms
  const highlightText = (text, searchTerm) => {
    if (!searchTerm || !text) return text;

    const regex = new RegExp(`(${searchTerm})`, "gi");
    const parts = text.split(regex);

    return parts.map((part, index) =>
      regex.test(part) ? (
        <span key={index} className="bg-yellow-200 font-medium">
          {part}
        </span>
      ) : (
        part
      )
    );
  };

  return (
    <div className="bg-[#ECEFF8] overflow-hidden border border-gray-200 hover:shadow-lg transition-shadow duration-300 flex flex-col h-full rounded-3xl">
      <div className="relative w-full h-48 overflow-hidden">
        <img
          className="object-cover w-full h-full"
          src={academy.academycoverImg.url}
          alt={academy.academyTitle}
        />
        <div className="absolute top-2 right-2 bg-[rgba(62,89,183,1)] text-white px-3 py-1 font-bold rounded-md">
          ${academy.academyPrice}
        </div>
      </div>
      <div className="p-4 flex flex-col flex-grow">
        <h3 ref={titleRef} className="text-lg font-bold text-gray-800 mb-2">
          {highlightText(academy.academyTitle, searchTerm)}
        </h3>
        <p
          className="text-gray-600 text-sm mb-4"
          style={{
            display: "-webkit-box",
            WebkitLineClamp: descriptionLines,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {highlightText(academy.academyDesc, searchTerm)}
        </p>
        <div className="flex justify-center mt-auto">
          <Link
            to={`/academy/professional-growth-courses/${titleToSlug(
              academy.academyTitle
            )}/`}
            className="bg-[rgba(62,89,183,1)] text-white px-24 md:px-10 lg:px-20 py-2 rounded-lg hover:bg-blue-700 transition text-nowrap"
          >
            Learn More
          </Link>
        </div>
      </div>
    </div>
  );
};

// Export for compatibility if needed
export { SearchContext };
export default ProfessionalCourse;
